import React, {KeyboardEvent, ReactNode, useState} from 'react';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './ArrAccordion.scss';

import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../arr-tooltip-wrapper/ArrTooltipWrapper';

export interface AccordionSection {
  id: string;
  title: string | React.ReactNode;
  content: React.ReactNode;
  isDisabled?: boolean;
  showInfoIcon?: boolean;
  tooltipText?: string;
  tooltipTitle?: string;
  toolTipTrigger?: 'hover' | 'click';
  isRequired?: boolean;
  infoIconClass?: string;
  headerRightContent?: ReactNode;
  titleTabIndex?: number;
  summaryTabIndex?: number;
  expandIconRole?: string;
  expandIconAriaHidden?: boolean;
  summaryRole?: string;
  summaryComponent?: React.ElementType;
}

export interface AccordionProps {
  sections: AccordionSection[];
  dataTestId: string;
  expandedSection?: string;
  onToggle?: (sectionId: string) => void;
  allowMultiple?: boolean;
  className?: string;
  isCustomSearchAccordian?: boolean;
  showExpandIcon?: boolean;
  headingLevel?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
}

const ArrAccordion: React.FC<AccordionProps> = ({
  sections,
  expandedSection,
  onToggle,
  allowMultiple = false,
  isCustomSearchAccordian = false,
  className,
  showExpandIcon = true,
  dataTestId,
  headingLevel = 'h3',
}) => {
  const [expanded, setExpanded] = useState<string[]>(
    expandedSection ? [expandedSection] : []
  );

  const handleToggle = (sectionId: string) => {
    const next = allowMultiple
      ? expanded.includes(sectionId)
        ? expanded.filter((id) => id !== sectionId)
        : [...expanded, sectionId]
      : expanded.includes(sectionId)
        ? []
        : [sectionId];

    setExpanded(next);
    onToggle?.(sectionId);
  };

  const handleKeyDown = (e: KeyboardEvent<any>, sectionId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(sectionId);
    }
  };

  return (
    <div
      className={`arr-accordion-container ${isCustomSearchAccordian ? 'arr-accordion-custom-search-accordion' : ''} ${className}`}
      data-testid={`${dataTestId}-arr-accordion-container`}
    >
      {sections.map((section, index) => {
        const isExpanded = expanded.includes(section.id);
        const SummaryComponent = section.summaryComponent || 'div';
        const ChevronIcon = ExpandMoreIcon;
        return (
          <MuiAccordion
            key={section.id}
            expanded={isExpanded}
            disabled={section.isDisabled}
            className={`arr-accordion-section ${section.isDisabled ? 'disabled' : ''}`}
            data-testid={`arr-accordion-${section.id}`}
            slotProps={{heading: {component: headingLevel}}}
          >
            <AccordionSummary
              expandIcon={
                !isCustomSearchAccordian && showExpandIcon ? (
                  <Box
                    className="arr-accordion-chevron-wrapper"
                    data-testid={`arr-chevron-wrapper-${section.id}`}
                  >
                    <ChevronIcon
                      className="arr-accordion-chevron"
                      data-testid={`arr-chevron-${section.id}`}
                      onClick={() =>
                        !section.isDisabled && handleToggle(section.id)
                      }
                      aria-label={isExpanded ? 'collapse' : 'expand-more'}
                      role={section.expandIconRole || 'button'}
                      aria-hidden={section.expandIconAriaHidden ?? false}
                    />
                  </Box>
                ) : null
              }
              onKeyDown={(e: KeyboardEvent<any>) =>
                handleKeyDown(e, section.id)
              }
              tabIndex={
                section.isDisabled ? -1 : (section.summaryTabIndex ?? 0)
              }
              role={section.summaryRole || 'button'}
              component={SummaryComponent}
              data-testid={`arr-summary-${section.id}`}
            >
              <Box
                className="arr-accordion-title-wrapper"
                data-testid={`arr-title-wrapper-${section.id}`}
              >
                {isCustomSearchAccordian && (
                  <ChevronIcon
                    className={`arr-accordion-chevron ${isExpanded ? 'arr-accordion-chevron-expanded' : ''}`}
                    data-testid={`arr-chevron-custom-search-${section.id}`}
                    onClick={() =>
                      !section.isDisabled && handleToggle(section.id)
                    }
                    aria-label={isExpanded ? 'collapse' : 'expand-more'}
                    role="button"
                    tabIndex={0}
                  />
                )}
                <Typography
                  className="arr-accordion-title"
                  data-testid={`arr-title-${section.id}`}
                  tabIndex={section.titleTabIndex ?? 0}
                >
                  {section.title}
                  {section.isRequired && (
                    <span
                      className="asterix"
                      data-testid={`arr-accordion-${dataTestId}-${index}`}
                    >
                      *
                    </span>
                  )}
                </Typography>
                {section.showInfoIcon && (
                  <ArrTooltipWrapper
                    title={section.tooltipTitle || section.tooltipText}
                    placement="top"
                    trigger={
                      <InfoIcon
                        role="button"
                        data-testid={`arr-info-icon-${section.id}`}
                        className={
                          section.infoIconClass
                            ? section.infoIconClass
                            : 'arr-accordion-info-icon'
                        }
                        tabIndex={0}
                      />
                    }
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: section.tooltipText || '',
                      }}
                      data-testid={`arr-accordion-inner-${dataTestId}-${index}`}
                    ></div>
                  </ArrTooltipWrapper>
                )}
              </Box>
              {isCustomSearchAccordian && section?.headerRightContent && (
                <Box tabIndex={0}>{section?.headerRightContent}</Box>
              )}
            </AccordionSummary>
            <AccordionDetails
              className="arr-accordion-details"
              data-testid={`arr-details-${section.id}`}
              style={{display: isExpanded ? 'block' : 'none'}}
            >
              {section.content}
            </AccordionDetails>
          </MuiAccordion>
        );
      })}
    </div>
  );
};

export default ArrAccordion;
